import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ASSIGN_VALUE } from '@/utils/constants';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';

interface IsrcInputProps {
  value: string | undefined | null;
  onChange: (value: string) => void;
  hasError?: boolean;
  validationState?: string;
  disabled?: boolean;
  modeSelectorDisabled?: boolean;
  hideModeSelector?: boolean;
}

export default function IsrcInput({
  value,
  onChange,
  hasError,
  validationState,
  disabled,
  modeSelectorDisabled,
  hideModeSelector,
}: IsrcInputProps) {
  const { t } = useTranslation();
  // Determine if the initial value implies "Assign" mode (ASSIGN_VALUE only)
  const isAssignMode = value === ASSIGN_VALUE;

  const [mode, setMode] = useState<'assign' | 'enter'>(
    isAssignMode ? 'assign' : 'enter'
  );
  const [isrcValue, setIsrcValue] = useState<string>(
    isAssignMode ? '' : value || ''
  );

  // Sync state if the value prop changes externally (e.g., when a new track loads)
  useEffect(() => {
    const currentIsAssign = value === ASSIGN_VALUE;
    setMode(currentIsAssign ? 'assign' : 'enter');
    if (!currentIsAssign) {
      setIsrcValue(value || '');
    }
  }, [value]);

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMode = e.target.value as 'assign' | 'enter';
    setMode(newMode);

    if (newMode === 'assign') {
      onChange(ASSIGN_VALUE);
    } else {
      // If changing to 'enter', use the existing isrcValue (removing the ASSIGN_VALUE word)
      const newValue = isrcValue === ASSIGN_VALUE ? '' : isrcValue;
      setIsrcValue(newValue);
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    if (
      /^([A-Z0]{0,2}|[A-Z0]{2}[A-Z0-9]{0,3}|[A-Z0]{2}[A-Z0-9]{3}\d{0,7})$/.test(
        newValue
      )
    ) {
      setIsrcValue(newValue);
      onChange(newValue);
    }
  };

  const validationClassName =
    validationState === VALIDATION_STATE_WARNING
      ? 'warning-input'
      : 'is-invalid invalid-input';

  return (
    <div className="identifier-input-container">
      {!hideModeSelector && (
        <select
          value={mode}
          onChange={handleDropdownChange}
          className={`form-select identifier-input-select ${hasError ? validationClassName : ''}`}
          disabled={disabled || modeSelectorDisabled}
        >
          <option value="enter">{t('track.enterIsrc')}</option>
          <option value="assign">{t('track.assignIsrc')}</option>
        </select>
      )}

      {(hideModeSelector || mode === 'enter') && (
        <input
          id="isrc"
          type="text"
          value={hideModeSelector ? value || '' : isrcValue}
          onChange={handleInputChange}
          className={`form-control ${hasError && !disabled ? validationClassName : ''}`}
          placeholder={t('track.enterIsrc')}
          disabled={disabled}
        />
      )}
    </div>
  );
}
